import { ForwardedRef, forwardRef } from "react";
import { Search, X } from "lucide-react";
import { FOCUS_RING, cx } from "./tokens";

export interface FilterSearchProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  /**
   * `section` narrows the options inside one section; `global` finds an option
   * anywhere. Two search fields may coexist only with these different jobs
   * (§23.2).
   */
  variant?: "section" | "global";
  /**
   * Keyboard hint, e.g. "Ctrl + K". Only pass this when the shortcut is
   * actually wired up — an advertised shortcut that does nothing is worse than
   * none (§12).
   */
  shortcut?: string;
  ariaLabel: string;
  id?: string;
  className?: string;
}

const FilterSearch = forwardRef(function FilterSearch(
  {
    value,
    onChange,
    placeholder,
    variant = "section",
    shortcut,
    ariaLabel,
    id,
    className,
  }: FilterSearchProps,
  ref: ForwardedRef<HTMLInputElement>,
) {
  const isGlobal = variant === "global";
  const showClear = value.length > 0;

  return (
    <div className={cx("relative", className)}>
      <Search
        className={cx(
          "pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted",
          isGlobal ? "h-[18px] w-[18px]" : "h-4 w-4",
        )}
        aria-hidden="true"
      />
      <input
        ref={ref}
        id={id}
        type="search"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        aria-label={ariaLabel}
        className={cx(
          "w-full rounded-input border border-edge text-ink placeholder:text-ink-muted",
          "transition-colors duration-150 hover:border-edge-strong",
          FOCUS_RING,
          // the browser's own clear affordance would sit next to ours
          "[&::-webkit-search-cancel-button]:appearance-none",
          isGlobal
            ? "h-12 bg-surface pl-10 pr-24 text-sm"
            : "h-10 bg-surface-muted pl-9 pr-9 text-[13px]",
        )}
      />

      {showClear ? (
        <button
          type="button"
          onClick={() => onChange("")}
          aria-label="Limpar busca"
          title="Limpar busca"
          className={cx(
            "absolute top-1/2 -translate-y-1/2 rounded-full p-1",
            "text-ink-muted transition-colors hover:bg-surface-hover hover:text-ink",
            FOCUS_RING,
            isGlobal ? "right-3" : "right-2",
          )}
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      ) : null}

      {shortcut && !showClear ? (
        <kbd
          className={cx(
            "pointer-events-none absolute right-3 top-1/2 -translate-y-1/2",
            "rounded border border-edge bg-surface-muted px-2 py-1",
            "font-sans text-[11px] font-medium text-ink-muted",
          )}
          aria-hidden="true"
        >
          {shortcut}
        </kbd>
      ) : null}
    </div>
  );
});

export default FilterSearch;
